import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { images } from '../../components/shared/assets';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { ContentfulText, ContentfulMultilineText } from '../../components/Contentful';
import { usePageContent, usePackages, useSiteSettings } from '../../hooks/useContentful';
import { FilterBar, TYPE_OPTIONS } from '../../components/shared/FilterBar';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import '../Packages/PackagesPage.css';

const PackagesAtacama: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDestination, setSelectedDestination] = useState<string>('atacama');
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  
  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('packages');
  const { data: packagesData } = usePackages();
  const { data: siteSettingsData } = useSiteSettings();
  const pageContent = pageContentData?.[0]?.fields as any;
  const packages = packagesData || [];
  const siteSettings = siteSettingsData?.[0]?.fields as any;

  // Filter packages for Atacama and apply additional filters
  const atacamaPackages = packages.filter((pkg: any) => {
    const packageFields = pkg.fields as any;
    const destination = packageFields.destination?.toLowerCase();
    const tags = packageFields.tags || [];
    
    // Must be Atacama destination
    const isAtacama = destination === 'atacama' || destination?.includes('atacama') || 
                      tags.some((tag: string) => tag.toLowerCase().includes('atacama'));
    if (!isAtacama) return false;
    
    // Apply type filter if selected
    if (selectedType) {
      const hasType = tags.some((tag: string) => 
        tag.toLowerCase().includes(selectedType.toLowerCase()) ||
        packageFields.category?.toLowerCase().includes(selectedType.toLowerCase())
      );
      if (!hasType) return false;
    }
    
    return true;
  });

  const handleViewMore = (packageId: string) => {
    navigate(`/atacama/packages/${packageId}`);
  };

  return (
    <div className="packages-page">
      {/* Navigation - Atacama variant with destination */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Hero Section */}
      <section className="packages-hero">
        <div className="packages-hero-image" style={{ backgroundImage: `url('/assets/atacama-factsheet-hero.jpg')` }}>
          <div className="packages-hero-content">
            <h1>
              <ContentfulText 
                content={pageContent?.heroTitle} 
                fallback="The Perfect Atacama Desert Adventure"
              />
            </h1>
            <p>
              <ContentfulMultilineText 
                content={pageContent?.heroDescription} 
                fallback="Every journey with Awasi Atacama immerses you in the rich stories, landscapes, and traditions of the Atacama Desert. Craft unforgettable, personal, and authentic experiences with the help of our local experts, ready to design your ideal itinerary through the world's driest desert."
              />
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="packages-filters">
        <FilterBar
          className="packages-filter-bar"
          dropdowns={[
            {
              id: 'destination',
              type: 'destination',
              level: 'brand',
              options: [{ id: 'atacama', value: 'atacama', label: 'ATACAMA' }],
              selectedValue: selectedDestination,
              onSelect: (value: string) => setSelectedDestination(value)
            },
            {
              id: 'type',
              type: 'type',
              level: 'brand',
              options: TYPE_OPTIONS,
              selectedValue: selectedType,
              onSelect: (value: string) => setSelectedType(value === selectedType ? undefined : value)
            }
          ]}
          alignment="center"
        />
        <HandDrawnButton
          text={pageContent?.combineOffersText || "COMBINE OFFERS"}
          variant="brand"
          color="awasi"
          animation="stroke-to-fill"
          onClick={() => navigate('/packages/combined')}
          className="combine-offers-btn"
        />
      </section>

      {/* Packages Grid */}
      <section className="packages-grid">
        <div className="packages-container">
          {atacamaPackages.length > 0 ? (
            atacamaPackages.map((pkg) => {
              const packageFields = pkg.fields as any;
              const tags = packageFields.tags || [];
              const imageUrl = packageFields.featuredImage?.fields?.file?.url;
              
              return (
                <div key={pkg.sys.id} className="package-card">
                  <div className="package-image">
                    <img src={imageUrl ? `https:${imageUrl}` : images.video1} alt={packageFields.title} />
                    <div className="package-tags">
                      {tags.map((tag: string, index: number) => (
                        <span key={index} className="tag">
                          <ContentfulText 
                            content={tag} 
                            fallback={tag}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="package-content">
                    <h3>
                      <ContentfulText 
                        content={packageFields.title} 
                        fallback="Package Title"
                      />
                    </h3>
                    <div className="package-links">
                      <CTALink
                        text={packageFields.buttonText || pageContent?.defaultButtonText || "VIEW MORE"}
                        type="primary"
                        color="awasi"
                        onClick={() => handleViewMore(packageFields.slug || pkg.sys.id)}
                        className="view-more"
                      />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            // Fallback static packages for Atacama
            <>
              <div className="package-card">
                <div className="package-image">
                  <img src={images.video1} alt="Desert Explorer Package" />
                  <div className="package-tags">
                    <span className="tag">ATACAMA</span>
                  </div>
                </div>
                <div className="package-content">
                  <h3>Desert Explorer - 3 Days</h3>
                  <div className="package-links">
                    <CTALink
                      text="VIEW MORE"
                      type="primary"
                      color="awasi"
                      onClick={() => handleViewMore('desert-explorer')}
                      className="view-more"
                    />
                  </div>
                </div>
              </div>

              <div className="package-card">
                <div className="package-image">
                  <img src={images.video2} alt="Astronomical Adventure" />
                  <div className="package-tags">
                    <span className="tag">ATACAMA</span>
                  </div>
                </div>
                <div className="package-content">
                  <h3>Astronomical Adventure - 4 Days</h3>
                  <div className="package-links">
                    <CTALink
                      text="VIEW MORE"
                      type="primary"
                      color="awasi"
                      onClick={() => handleViewMore('astronomical-adventure')}
                      className="view-more"
                    />
                  </div>
                </div>
              </div>

              <div className="package-card">
                <div className="package-image">
                  <img src={images.video3} alt="Cultural Heritage" />
                  <div className="package-tags">
                    <span className="tag">ATACAMA</span>
                  </div>
                </div>
                <div className="package-content">
                  <h3>Cultural Heritage - 5 Days</h3>
                  <div className="package-links">
                    <CTALink
                      text="VIEW MORE"
                      type="primary"
                      color="awasi"
                      onClick={() => handleViewMore('cultural-heritage')}
                      className="view-more"
                    />
                  </div>
                </div>
              </div>

              <div className="package-card">
                <div className="package-image">
                  <img src={images.video4} alt="Complete Atacama" />
                  <div className="package-tags">
                    <span className="tag">ATACAMA</span>
                  </div>
                </div>
                <div className="package-content">
                  <h3>Complete Atacama - 7 Days</h3>
                  <div className="package-links">
                    <CTALink
                      text="VIEW MORE"
                      type="primary"
                      color="awasi"
                      onClick={() => handleViewMore('complete-atacama')}
                      className="view-more"
                    />
                  </div>
                </div>
              </div>

              <div className="package-card">
                <div className="package-image">
                  <img src="/assets/experiencepagebreak.jpg" alt="RE-ENERGIZE Retreat" />
                  <div className="package-tags">
                    <span className="tag">ATACAMA</span>
                  </div>
                </div>
                <div className="package-content">
                  <h3>RE-ENERGIZE wellness immersion</h3>
                  <div className="package-links">
                    <CTALink
                      text="VIEW MORE"
                      type="primary"
                      color="awasi"
                      onClick={() => handleViewMore('re-energize-atacama')}
                      className="view-more"
                    />
                  </div>
                </div>
              </div>

              <div className="package-card">
                <div className="package-image">
                  <img src="/assets/fullviewportatacama.jpg" alt="Combine with Patagonia" />
                  <div className="package-tags">
                    <span className="tag">ATACAMA / PATAGONIA</span>
                  </div>
                </div>
                <div className="package-content">
                  <h3>Combine Atacama & Patagonia</h3>
                  <div className="package-links">
                    <CTALink
                      text="VIEW MORE"
                      type="primary"
                      color="awasi"
                      onClick={() => handleViewMore('atacama-patagonia')}
                      className="view-more"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        <div className="packages-pagination">
          <button className="page-btn active">1</button>
          <button className="page-btn">2</button>
          <button className="page-btn">3</button>
          <button className="page-btn">4</button>
        </div>
      </section>

      {/* Instagram Section */}
      <InstagramSection 
        title="Follow us on Instagram @awasiexperience"
        posts={[
          { src: images.film13, alt: "Instagram post 1" },
          { src: images.filmVideo1, alt: "Instagram post 2", isVideo: true },
          { src: images.film14, alt: "Instagram post 3" },
          { src: images.film15, alt: "Instagram post 4" }
        ]}
      />

      {/* Footer - Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default PackagesAtacama;